import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { gap } from '../content';
import { heroUnits, type HeroUnit } from '../heroUnits';
import { cx, prefersReducedMotion } from '../ui';

// THE GAP: the central argument of the site, told for one unit in three beats.
//  1. Before the sale: every system you run knows the unit, and all of them run up to one line (the factory gate, the till).
//     Shown at once (no empty board): the lanes are drawn, the product's later life sits faint beyond the line.
//  2. After the sale: every lane stops dead. The life carries on in the dark, questions appear; the support call has no context.
//  3. With a digital identity: one gradient line crosses the gate and runs the whole life. The life lights up,
//     the questions are ticked off, and the support call fills itself (the ticket is the payoff, it carries the answers).
// Wide: diagram left, caption + support call right. Narrow: a compact vertical timeline, caption and a 4-row call below.
// Starts when scrolled into view; the steps can be clicked. Reduced motion: final beat, static.

export type Tone = 'light' | 'dark';
export type TheGapProps = {
  tone?: Tone;
  unit?: HeroUnit;
  compact?: boolean;
  className?: string;
  /** Beat 3 caption uses content.gap.answer.title. GapSection sets false when it shows the answer itself right below. */
  answerCaption?: boolean;
};

const FALLBACK_IMG = '/images/v2/gen/heatpump-commission-b-thumb.jpg';
const BEAT_MS = [0, 0, 2300, 6400]; // when each beat starts, after the diagram is in view

// Lane markers: what each system knows before the line (story copy, same voice as content.ts).
const LANE_MARK = ['Serial issued', 'Built and tested', 'Order placed', 'Sold', 'Shipped'];
// The unknowns after the line (indices into gap.after.unknowns), one per lane.
const LANE_Q = [1, 0, 3, 4, 2];
// The product's life after the line.
const LIFE = ['Installed', 'In use', 'Fault', 'Sold on', 'Recycled'];
const lifeDetail = (u: HeroUnit) => [u.partner, 'Owner registered', 'Case opened', 'New owner', 'Materials data'];

const ticketRows = (u: HeroUnit) => [
  { k: gap.context[0], v: <>{u.model} · <span className="g-nw">{u.serial}</span></> },
  { k: gap.context[1], v: u.owner2 },
  { k: gap.context[2], v: `Active, from ${u.handoverName.toLowerCase()}` },
  { k: gap.context[3], v: u.partner },
  { k: gap.context[4], v: 'Serviced once, on record' },
  { k: gap.context[5], v: 'One, record transferred' },
];

const STEPS = [gap.before.label, gap.after.label, 'With a digital identity'];
const CAPTIONS = [
  { title: 'Your systems know every unit.', body: 'Built, tested, ordered, sold and shipped. All of it runs up to one line.' },
  { title: 'Then every system stops.', body: 'The product is installed, used, fixed and sold on, and nobody knows. Every support call starts from nothing.' },
  { title: gap.answer.title.join(' '), body: 'One line from the factory to the last owner. Support starts with the full story.' },
];
const CAPTION_3_ALT = { title: 'The context travels with it.', body: 'One line from the factory to the last owner. Whoever takes the call starts with the full story.' };

// ---------------------------------------------------------------- geometry
type Pt = { x: number; y: number };
type Geo = {
  dir: 'h' | 'v';
  W: number;
  H: number;
  lanes: { label: string; a: Pt; b: Pt; mark: Pt; band: { x: number; y: number; w: number; h: number }; labelAt: Pt }[];
  gate: { a: Pt; b: Pt; pill: Pt };
  track: { a: Pt; g: Pt; b: Pt; labelAt?: Pt };
  life: Pt[];
  qs: (Pt & { max?: number })[];
  lifeW: number;
  stagger?: boolean;
  token: Pt[]; // per beat 0..3 (centre of the photo)
  dim: { x: number; y: number; w: number; h: number };
};

function geoH(W: number, compact: boolean, roomy = false): Geo {
  const x0 = 148;
  const x1 = W - 6;
  const xg = Math.round(x0 + (x1 - x0) * 0.36);
  const top = 62;
  const dy = roomy ? (compact ? 36 : 42) : compact ? 28 : 31;
  const ys = [0, 1, 2, 3, 4].map((i) => top + i * dy);
  const ty = ys[4] + (roomy ? 82 : 64);
  const H = ty + (compact ? 52 : 60);
  const span = x1 - xg;
  const lifeX = [0.1, 0.3, 0.5, 0.7, 0.9].map((f) => Math.round(xg + span * f));
  const qx = [0.02, 0.36, 0.1, 0.46, 0.2];
  const lifeW = Math.round(span * 0.2) - 4;
  return {
    dir: 'h',
    W,
    H,
    lanes: gap.before.systems.map((label, i) => ({
      label,
      a: { x: x0, y: ys[i] },
      b: { x: xg, y: ys[i] },
      mark: { x: Math.round(x0 + 14 + (xg - x0) * (0.02 + i * 0.13)), y: ys[i] },
      band: { x: 0, y: ys[i] - 12, w: xg, h: 24 },
      labelAt: { x: 12, y: ys[i] },
    })),
    gate: { a: { x: xg, y: 18 }, b: { x: xg, y: H - 6 }, pill: { x: xg, y: 18 } },
    track: { a: { x: x0, y: ty }, g: { x: xg, y: ty }, b: { x: x1, y: ty }, labelAt: { x: 12, y: ty } },
    life: lifeX.map((x) => ({ x, y: ty })),
    qs: qx.map((f, i) => {
      const x = Math.round(xg + 18 + span * f);
      return { x, y: ys[i], max: x1 - x - 6 };
    }),
    lifeW,
    token: [
      { x: x0 + 24, y: ty - 32 },
      { x: xg - 150, y: ty - 32 },
      { x: lifeX[2] - 16, y: ty - 32 },
      { x: lifeX[2] - 16, y: ty - 32 },
    ],
    dim: { x: xg, y: 34, w: x1 - xg + 6, h: H - 34 },
  };
}

function geoV(W: number, compact: boolean): Geo {
  const px = 20;
  const c0 = 72;
  const stagger = (W - 20 - c0) / 4 < 60;
  const c1 = W - (stagger ? 30 : 22);
  const xs = [0, 1, 2, 3, 4].map((i) => Math.round(c0 + ((c1 - c0) * i) / 4));
  const top = stagger ? 66 : 52;
  const gy = top + (compact ? 70 : 84);
  const step = compact ? 40 : 44;
  const e0 = gy + 42;
  const lifeY = [0, 1, 2, 3, 4].map((i) => e0 + i * step);
  const H = lifeY[4] + 30;
  return {
    dir: 'v',
    W,
    H,
    lanes: gap.before.systems.map((label, i) => ({
      label,
      a: { x: xs[i], y: top },
      b: { x: xs[i], y: gy },
      mark: { x: xs[i], y: Math.round(top + (gy - top) * (0.2 + i * 0.14)) },
      band: { x: xs[i] - 12, y: 10, w: 24, h: gy - 10 },
      labelAt: { x: xs[i], y: stagger && i % 2 ? 30 : 15 },
    })),
    gate: { a: { x: 0, y: gy }, b: { x: W, y: gy }, pill: { x: px + 22, y: gy } },
    track: { a: { x: px, y: top - 4 }, g: { x: px, y: gy }, b: { x: px, y: H - 8 } },
    life: lifeY.map((y) => ({ x: px, y })),
    qs: lifeY.map((y) => ({ x: W, y: y - step / 2 + 1, max: W - 110 })),
    lifeW: 0,
    stagger,
    token: [
      { x: px, y: top + 8 },
      { x: px, y: gy - 30 },
      { x: px, y: lifeY[2] },
      { x: px, y: lifeY[2] },
    ],
    dim: { x: 0, y: gy, w: W, h: H - gy },
  };
}

// ---------------------------------------------------------------- hooks
function useWidth<T extends HTMLElement>(ref: React.RefObject<T>) {
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setW(el.clientWidth);
    if (!('ResizeObserver' in window)) return;
    const ro = new ResizeObserver(([e]) => setW(Math.round(e.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return w;
}

// Beat machine. Beat 1 is shown at once (static); once in view the unit travels to the line, then 2, then 3.
// Reduced motion: 3 at once.
function useBeats(rootRef: React.RefObject<HTMLElement>) {
  const [beat, setBeat] = useState(1);
  const [moving, setMoving] = useState(false);
  const [rm, setRm] = useState(false);
  const timers = useRef<number[]>([]);
  const clear = () => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
  };
  useEffect(() => {
    if (prefersReducedMotion()) {
      setRm(true);
      setBeat(3);
      return;
    }
    const el = rootRef.current;
    if (!el || !('IntersectionObserver' in window)) {
      setBeat(3);
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          io.disconnect();
          setMoving(true);
          timers.current = [2, 3].map((b) => window.setTimeout(() => setBeat(b), BEAT_MS[b]));
        }
      },
      { threshold: 0, rootMargin: '0px 0px -35% 0px' }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const go = (b: number) => {
    clear();
    setMoving(true);
    setBeat(b);
  };
  return { beat, moving, rm, go };
}

// ---------------------------------------------------------------- pieces
function UnitPhoto({ unit, size }: { unit: HeroUnit; size: number }) {
  const [src, setSrc] = useState(unit.product);
  useEffect(() => setSrc(unit.product), [unit.product]);
  return (
    <img
      src={src}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      className="g-photo"
      style={src === FALLBACK_IMG ? { objectPosition: '88% 50%' } : undefined}
      onError={() => src !== FALLBACK_IMG && setSrc(FALLBACK_IMG)}
    />
  );
}

function Diagram({ g, unit, tone }: { g: Geo; unit: HeroUnit; tone: Tone }) {
  const id = React.useId().replace(/:/g, '');
  const h = g.dir === 'h';
  const details = lifeDetail(unit);
  const ink = tone === 'dark' ? '#fff' : '#241E61';
  const line = (a: Pt, b: Pt) => `M${a.x} ${a.y} L${b.x} ${b.y}`;
  return (
    <div className="g-diagram" style={{ height: g.H }}>
      {/* Behind: the after-zone, dark once the lanes stop */}
      <div className="g-dim" style={{ left: g.dim.x, top: g.dim.y, width: g.dim.w, height: g.dim.h }} />
      {g.lanes.map((l, i) => (
        <div key={`band${i}`} className="g-band" style={{ left: l.band.x, top: l.band.y, width: l.band.w, height: l.band.h }} />
      ))}

      <svg className="g-svg" width={g.W} height={g.H} viewBox={`0 0 ${g.W} ${g.H}`} aria-hidden="true">
        <defs>
          <linearGradient id={`gl${id}`} gradientUnits="userSpaceOnUse" x1={g.track.a.x} y1={g.track.a.y} x2={g.track.b.x} y2={g.track.b.y}>
            <stop offset="0" stopColor="#3FAE6C" />
            <stop offset=".55" stopColor="#8FD53A" />
            <stop offset="1" stopColor="#CFF710" />
          </linearGradient>
        </defs>
        {/* lanes: run up to the gate; end in a cap once the sale is made */}
        {g.lanes.map((l, i) => (
          <g key={i}>
            <path className="g-lane" d={line(l.a, l.b)} stroke={ink} />
            <circle className="g-lane-start" cx={l.a.x} cy={l.a.y} r={3} />
            <path
              className="g-cap"
              d={h ? `M${l.b.x - 3} ${l.b.y - 7} L${l.b.x - 3} ${l.b.y + 7}` : `M${l.b.x - 7} ${l.b.y - 3} L${l.b.x + 7} ${l.b.y - 3}`}
              stroke={ink}
            />
            {!h && <rect className="g-mark" x={l.mark.x - 3} y={l.mark.y - 3} width={6} height={6} rx={1.5} fill={ink} />}
          </g>
        ))}
        {/* the product's own line: solid to the gate, dotted (unknown) after it */}
        <path className="g-track-before" d={line(g.track.a, g.track.g)} stroke={ink} />
        <path className="g-track-after" d={line(g.track.g, g.track.b)} />
        {/* the identity: one gradient line, drawn once, on the payoff */}
        <path className="g-identity" d={line(g.track.a, g.track.b)} pathLength={1} stroke={`url(#gl${id})`} />
        {g.life.map((p, i) => (
          <g key={i} className="g-node" style={{ ['--i' as string]: i }}>
            <circle cx={p.x} cy={p.y} r={5.5} className="g-node-o" />
            <circle cx={p.x} cy={p.y} r={2.5} className="g-node-i" />
          </g>
        ))}
        {/* the gate */}
        <path className="g-gate" d={line(g.gate.a, g.gate.b)} stroke={ink} />
      </svg>

      <div className={cx('g-pill', !h && 'is-v')} style={{ left: g.gate.pill.x, top: g.gate.pill.y }}>
        {gap.line}
      </div>

      {/* system labels and what each knows */}
      {g.lanes.map((l, i) => (
        <React.Fragment key={i}>
          <div className={cx('g-sys', !h && 'is-v', g.stagger && 'is-st')} style={{ left: l.labelAt.x, top: l.labelAt.y }}>
            {h && <span className="g-live" />}
            {l.label}
          </div>
          {h && (
            <div className="g-marklabel" style={{ left: l.mark.x, top: l.mark.y }}>
              {LANE_MARK[i]}
            </div>
          )}
        </React.Fragment>
      ))}

      {h && g.track.labelAt && (
        <div className="g-sys g-sys-product" style={{ left: g.track.labelAt.x, top: g.track.labelAt.y }}>
          This {unit.noun}
        </div>
      )}

      {/* the unknowns: asked in the dark, ticked off once the identity runs past the line */}
      {g.qs.map((p, i) => (
        <div
          key={i}
          className={cx('g-q', !h && 'is-v')}
          style={{ left: h ? p.x : undefined, right: h ? undefined : 0, top: p.y, maxWidth: p.max, ['--i' as string]: i } as React.CSSProperties}
        >
          <i />
          {gap.after.unknowns[LANE_Q[i]]}
        </div>
      ))}

      {/* the product's life after the line */}
      {g.life.map((p, i) => (
        <div
          key={i}
          className={cx('g-life', !h && 'is-v')}
          style={{ left: h ? p.x : p.x + 24, top: h ? p.y + 12 : p.y, width: h ? g.lifeW : undefined, ['--i' as string]: i } as React.CSSProperties}
        >
          <span className="g-life-t">{LIFE[i]}</span>
          {(!h || g.lifeW >= 60) && <span className="g-life-d">{details[i]}</span>}
        </div>
      ))}

      <Token g={g} unit={unit} />
    </div>
  );
}

function Token({ g, unit }: { g: Geo; unit: HeroUnit }) {
  const h = g.dir === 'h';
  const vars = Object.fromEntries(g.token.flatMap((p, i) => [[`--t${i}x`, `${p.x}px`], [`--t${i}y`, `${p.y}px`]])) as React.CSSProperties;
  return (
    <div className={cx('g-token', !h && 'is-v')} style={vars}>
      <span className="g-token-ring">
        <UnitPhoto unit={unit} size={h ? 40 : 32} />
        <span className="g-token-q">?</span>
      </span>
      {h && (
        <span className="g-token-meta">
          <span className="g-token-model">{unit.model}</span>
          <span className="g-token-serial">
            <span className="g-ser-known">{unit.serial}</span>
            <span className="g-ser-lost">Owner unknown</span>
          </span>
        </span>
      )}
    </div>
  );
}

function Ticket({ unit, rows }: { unit: HeroUnit; rows: number }) {
  return (
    <div className="g-ticket" aria-hidden="true">
      <div className="g-ticket-head">
        <span className="g-ticket-title">Support call</span>
        <span className="g-st g-st-empty">
          <i />
          No context
        </span>
        <span className="g-st g-st-full">
          <i />
          From the rating plate
        </span>
      </div>
      <div className="g-ticket-quote">&ldquo;It has stopped working. Can someone come out?&rdquo;</div>
      <dl className="g-rows">
        {ticketRows(unit)
          .slice(0, rows)
          .map((r, i) => (
            <div key={i} className="g-row" style={{ ['--i' as string]: i } as React.CSSProperties}>
              <dt>{r.k}</dt>
              <dd>
                <span className="g-v-empty" />
                <span className="g-v">{r.v}</span>
              </dd>
            </div>
          ))}
      </dl>
    </div>
  );
}

// ---------------------------------------------------------------- main
export function TheGap({ tone = 'light', unit = heroUnits[0], compact = false, className, answerCaption = true }: TheGapProps) {
  const rootRef = useRef<HTMLElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const FW = useWidth(rootRef);
  const W = useWidth(stageRef);
  const { beat, moving, rm, go } = useBeats(rootRef);
  // Side layout (call beside the diagram) when the figure is wide enough to give the diagram ~640px.
  const side = FW >= 1040;
  // Orientation with hysteresis, so a scrollbar appearing near the breakpoint cannot make it flip back and forth.
  const dirRef = useRef<'h' | 'v' | null>(null);
  if (W) {
    if (dirRef.current === null) dirRef.current = W >= 600 ? 'h' : 'v';
    else if (dirRef.current === 'h' && W < 570) dirRef.current = 'v';
    else if (dirRef.current === 'v' && W >= 630) dirRef.current = 'h';
  }
  const vert = dirRef.current === 'v';
  const g = W ? (vert ? geoV(W, compact) : geoH(W, compact, side)) : null;
  const caps = [CAPTIONS[0], CAPTIONS[1], answerCaption ? CAPTIONS[2] : CAPTION_3_ALT];
  const rows = vert || compact ? 4 : 6;
  const summary = `${gap.before.label}: ${gap.before.systems.join(', ')} all run up to ${gap.line}. ${gap.after.label}: every system stops there, while the product is installed, used, develops a fault, is sold on and recycled. ${gap.after.unknowns.join(' ')} ${gap.answer.title.join(' ')} One identity runs the whole life, so support starts with ${gap.context.join(', ').toLowerCase()}.`;

  return (
    <figure
      ref={rootRef}
      className={cx(
        'bm-gap',
        `tone-${tone}`,
        compact && 'is-compact',
        rm && 'rm',
        moving && 'go',
        'b1',
        beat >= 2 && 'b2',
        beat >= 3 && 'b3',
        vert && 'is-vert',
        side && 'is-side',
        className
      )}
    >
      <style>{CSS}</style>
      <figcaption className="sr-only">{summary}</figcaption>

      <div className="g-steps" role="tablist" aria-label="The gap, in three steps">
        {STEPS.map((label, i) => (
          <button
            key={label}
            type="button"
            role="tab"
            aria-selected={beat === i + 1}
            className={cx('g-step', beat === i + 1 && 'is-on', beat > i + 1 && 'is-done')}
            onClick={() => go(i + 1)}
          >
            <span className="g-step-bar">
              <span />
            </span>
            <span className="g-step-n">{i + 1}</span>
            <span className="g-step-l">{label}</span>
          </button>
        ))}
      </div>

      <div className="g-body">
        <div ref={stageRef} className="g-stage" aria-hidden="true">
          {g && <Diagram g={g} unit={unit} tone={tone} />}
        </div>
        {/* all three captions share one cell, so the height never jumps between beats */}
        <div className="g-caps" aria-live="polite">
          {caps.map((c, i) => (
            <div key={i} className={cx('g-capt', beat === i + 1 && 'is-on')} aria-hidden={beat !== i + 1}>
              <p className="g-cap-t">{c.title}</p>
              <p className="g-cap-b">{c.body}</p>
            </div>
          ))}
        </div>
        <Ticket unit={unit} rows={rows} />
      </div>
    </figure>
  );
}

// ---------------------------------------------------------------- styles (scoped to .bm-gap)
const CSS = `
.bm-gap{--ink:#241E61;--txt:#232047;--mut:#67648F;--faint:#A9A7C4;--ghost:#C9C7DE;--rule:#E4E3F0;--card:#fff;--band:#F5F5FB;--dim:#F3F2F8;--hatch:#E9E8F2;--q:#4C4970;--qb:#C9C7DE;--tk:#fff;
  position:relative;margin:0;border-radius:20px;background:var(--card);box-shadow:0 0 0 1px var(--rule),0 30px 60px -40px rgba(24,19,67,.35);padding:20px 26px 24px;color:var(--txt);font-family:Poppins,system-ui,sans-serif}
.bm-gap.tone-dark{--ink:#fff;--txt:#fff;--mut:rgba(255,255,255,.68);--faint:rgba(255,255,255,.4);--ghost:rgba(255,255,255,.3);--rule:rgba(255,255,255,.12);--card:#181343;--band:rgba(255,255,255,.055);--dim:rgba(0,0,0,.24);--hatch:rgba(255,255,255,.05);--q:rgba(255,255,255,.84);--qb:rgba(255,255,255,.3);--tk:#211B58;box-shadow:0 0 0 1px var(--rule),0 40px 80px -40px rgba(0,0,0,.6)}
.bm-gap .sr-only{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}
.bm-gap *{box-sizing:border-box}

/* steps */
.bm-gap .g-steps{display:grid;grid-template-columns:repeat(3,1fr);gap:14px;margin-bottom:14px}
.bm-gap .g-step{appearance:none;background:none;border:0;padding:0;text-align:left;cursor:pointer;color:var(--mut);font:inherit;display:grid;grid-template-columns:auto 1fr;column-gap:8px;align-items:baseline}
.bm-gap .g-step:focus-visible{outline:2px solid #2F9E58;outline-offset:4px;border-radius:4px}
.bm-gap .g-step-bar{grid-column:1/-1;display:block;height:2px;background:var(--rule);border-radius:2px;overflow:hidden;margin-bottom:9px}
.bm-gap .g-step-bar span{display:block;height:100%;width:100%;background:var(--ink);transform:scaleX(0);transform-origin:left}
.bm-gap .g-step.is-done .g-step-bar span,.bm-gap .g-step.is-on .g-step-bar span{transform:scaleX(1)}
.bm-gap.go .g-step.is-on .g-step-bar span{transition:transform .5s var(--bm-ease)}
.bm-gap.b3 .g-step:last-child .g-step-bar span{background:var(--bm-gradient)}
.bm-gap .g-step-n{font-size:12.5px;font-weight:600;font-variant-numeric:tabular-nums}
.bm-gap .g-step-l{font-size:13.5px;font-weight:600;letter-spacing:-.005em}
.bm-gap .g-step.is-on,.bm-gap .g-step.is-done{color:var(--txt)}
.bm-gap .g-step:hover .g-step-l{text-decoration:underline;text-underline-offset:3px;text-decoration-thickness:1px}

/* body: diagram, then (wide) caption + call beside it, (medium) below it in two columns, (narrow) stacked */
.bm-gap .g-body{display:grid;grid-template-columns:minmax(0,5fr) minmax(0,6fr);grid-template-areas:"stage stage" "caps ticket";gap:22px 32px;align-items:start}
.bm-gap .g-stage{grid-area:stage}
.bm-gap .g-caps{grid-area:caps}
.bm-gap .g-ticket{grid-area:ticket}
.bm-gap.is-side .g-body{grid-template-columns:minmax(0,1fr) 392px;grid-template-rows:auto 1fr;grid-template-areas:"stage caps" "stage ticket";column-gap:36px;row-gap:16px}
.bm-gap.is-side .g-stage{align-self:start;padding-top:6px}
.bm-gap.is-side .g-row{grid-template-columns:154px minmax(0,1fr)}
.bm-gap.is-side .g-ticket{padding:12px 14px 3px}
.bm-gap.is-side .g-cap-t{font-size:23px}
.bm-gap.is-side .g-row dt{white-space:nowrap}
.bm-gap.is-vert .g-body{grid-template-columns:minmax(0,1fr);grid-template-areas:"stage" "caps" "ticket"}

/* stage */
.bm-gap .g-stage{position:relative;width:100%;min-height:1px}
.bm-gap .g-diagram{position:relative;width:100%}
.bm-gap .g-svg{position:absolute;inset:0;overflow:visible}
.bm-gap .g-band{position:absolute;border-radius:7px;background:var(--band)}
.bm-gap .g-dim{position:absolute;border-radius:0 12px 12px 0;background:var(--dim) repeating-linear-gradient(135deg,transparent 0 9px,var(--hatch) 9px 10px);visibility:hidden}
.bm-gap.is-vert .g-dim{border-radius:0 0 12px 12px}
.bm-gap.b2 .g-dim{visibility:visible}
.bm-gap.b3 .g-dim{background:var(--dim);opacity:.55}

.bm-gap .g-lane{fill:none;stroke-width:1.5;opacity:.9}
.bm-gap .g-lane-start{fill:#3FAE6C}
.bm-gap .g-cap{stroke-width:2.5;fill:none;visibility:hidden}
.bm-gap.b2 .g-cap{visibility:visible}
.bm-gap .g-gate{stroke-width:3;stroke-linecap:round}

.bm-gap .g-track-before{fill:none;stroke-width:2}
.bm-gap .g-track-after{fill:none;stroke:var(--faint);stroke-width:2;stroke-dasharray:1.5 7;stroke-linecap:round}
.bm-gap .g-identity{fill:none;stroke-width:5;stroke-linecap:round;stroke-dasharray:1;stroke-dashoffset:1;visibility:hidden}
.bm-gap.b3 .g-identity{visibility:visible;stroke-dashoffset:0;transition:stroke-dashoffset 1.1s var(--bm-ease) .1s}

.bm-gap .g-node-o{fill:var(--card);stroke:var(--ghost);stroke-width:1.5}
.bm-gap .g-node-i{fill:var(--ghost)}
.bm-gap.b2 .g-node-o{stroke:var(--faint)}
.bm-gap.b2 .g-node-i{fill:var(--faint)}
.bm-gap.b3 .g-node-o{stroke:var(--ink);stroke-width:2;transition:stroke 0s calc(300ms + var(--i)*160ms)}
.bm-gap.b3 .g-node-i{fill:var(--ink);transition:fill 0s calc(300ms + var(--i)*160ms)}

/* labels */
.bm-gap .g-pill{position:absolute;transform:translate(-50%,-50%);background:var(--ink);color:var(--card);font-size:12px;font-weight:600;letter-spacing:-.005em;padding:5px 12px;border-radius:999px;white-space:nowrap;z-index:1}
.bm-gap.tone-dark .g-pill{color:#181343}
.bm-gap .g-pill.is-v{transform:translateY(-50%);font-size:11.5px;padding:4px 10px}
.bm-gap .g-sys{position:absolute;transform:translateY(-50%);font-size:13px;font-weight:600;color:var(--txt);white-space:nowrap;display:flex;align-items:center;gap:8px;letter-spacing:-.01em}
.bm-gap .g-sys.is-v{transform:translate(-50%,-50%);font-size:10px;line-height:1.15;letter-spacing:-.02em;white-space:normal;text-align:center;width:56px;display:block}
.bm-gap .g-sys.is-v.is-st{width:auto;white-space:nowrap;letter-spacing:0;word-spacing:.08em}
.bm-gap .g-sys-product{color:var(--mut);font-weight:500}
.bm-gap .g-live{width:6px;height:6px;border-radius:50%;background:#3FAE6C;box-shadow:0 0 0 3px rgba(63,174,108,.18)}
.bm-gap .g-marklabel{position:absolute;transform:translateY(-50%);font-size:11px;font-weight:500;line-height:1;color:var(--mut);white-space:nowrap;background:var(--band);padding:3px 5px;border-radius:4px}
.bm-gap.tone-dark .g-marklabel{background:#221D52}

.bm-gap .g-q{position:absolute;transform:translateY(-50%);display:flex;align-items:center;gap:7px;font-size:12.5px;font-weight:500;line-height:1.2;color:var(--q);padding:4px 10px;border-radius:999px;border:1px dashed var(--qb);background:var(--card);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;visibility:hidden}
.bm-gap .g-q i{flex:none;display:block;width:7px;height:7px;border-radius:50%;box-shadow:inset 0 0 0 1.5px var(--faint)}
.bm-gap.b2 .g-q{visibility:visible;animation:g-in-q .3s var(--bm-ease) calc(250ms + var(--i)*320ms) both}
.bm-gap.b3 .g-q{animation:g-cut .01s calc(500ms + var(--i)*160ms) both;border-style:solid;border-color:var(--rule);color:var(--txt)}
.bm-gap.b3 .g-q i{background:var(--bm-gradient);box-shadow:none}
.bm-gap .g-q.is-v{font-size:11px;padding:3px 8px;gap:6px}
.bm-gap .g-q.is-v i{width:6px;height:6px}

.bm-gap .g-life{position:absolute;transform:translateX(-50%);text-align:center;display:flex;flex-direction:column;gap:1px}
.bm-gap .g-life.is-v{transform:translateY(-50%);flex-direction:row;align-items:baseline;gap:8px;text-align:left;white-space:nowrap}
.bm-gap .g-life-t{font-size:12.5px;font-weight:600;color:var(--ghost)}
.bm-gap.b2 .g-life-t{color:var(--faint)}
.bm-gap .g-life-d{font-size:11px;line-height:1.25;color:var(--mut);visibility:hidden;display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:2;overflow:hidden}
.bm-gap .g-life.is-v .g-life-d{display:inline;-webkit-line-clamp:unset}
.bm-gap .g-life.is-v .g-life-d{font-size:11.5px}
.bm-gap.b3 .g-life-t{color:var(--txt);transition:color 0s calc(300ms + var(--i)*160ms)}
.bm-gap.b3 .g-life-d{visibility:visible;animation:g-cut .01s calc(300ms + var(--i)*160ms) both}

/* token */
.bm-gap .g-token{position:absolute;left:0;top:0;display:flex;align-items:center;gap:9px;transform:translate(calc(var(--t0x) - 21px),calc(var(--t0y) - 21px));z-index:2}
.bm-gap.go .g-token{transform:translate(calc(var(--t1x) - 21px),calc(var(--t1y) - 21px));transition:transform 1.4s var(--bm-ease) .2s}
.bm-gap.b2 .g-token{transform:translate(calc(var(--t2x) - 21px),calc(var(--t2y) - 21px));transition:transform 1.2s var(--bm-ease) .05s}
.bm-gap.b3 .g-token{transform:translate(calc(var(--t3x) - 21px),calc(var(--t3y) - 21px))}
.bm-gap .g-token.is-v{transform:translate(calc(var(--t0x) - 17px),calc(var(--t0y) - 17px))}
.bm-gap.go .g-token.is-v{transform:translate(calc(var(--t1x) - 17px),calc(var(--t1y) - 17px))}
.bm-gap.b2 .g-token.is-v{transform:translate(calc(var(--t2x) - 17px),calc(var(--t2y) - 17px))}
.bm-gap.b3 .g-token.is-v{transform:translate(calc(var(--t3x) - 17px),calc(var(--t3y) - 17px))}
.bm-gap .g-token-ring{position:relative;display:block;padding:2px;border-radius:12px;background:var(--card);box-shadow:0 0 0 1px var(--rule),0 10px 24px -12px rgba(24,19,67,.5)}
.bm-gap.b3 .g-token-ring{background:var(--bm-gradient);box-shadow:0 10px 24px -12px rgba(24,19,67,.5)}
.bm-gap .g-photo{display:block;width:38px;height:38px;border-radius:10px;object-fit:cover;background:#E9E8F2}
.bm-gap .g-token.is-v .g-photo{width:30px;height:30px;border-radius:8px}
.bm-gap.b2:not(.b3) .g-photo{filter:grayscale(1) contrast(.8) brightness(1.08);opacity:.75}
.bm-gap .g-token-q{position:absolute;right:-7px;top:-7px;width:18px;height:18px;border-radius:50%;background:var(--card);color:var(--mut);box-shadow:0 0 0 1px var(--qb);font-size:11px;font-weight:700;display:grid;place-items:center;visibility:hidden}
.bm-gap .g-token.is-v .g-token-q{right:-9px;top:-6px;width:16px;height:16px;font-size:10px}
.bm-gap.b2:not(.b3) .g-token-q{visibility:visible}
.bm-gap .g-token-meta{display:flex;flex-direction:column;line-height:1.2;background:var(--card);padding:3px 8px 3px 0;border-radius:6px}
.bm-gap .g-token-model{font-size:12.5px;font-weight:700;color:var(--txt);letter-spacing:-.01em}
.bm-gap .g-token-serial{font-family:'Geist Mono',ui-monospace,monospace;font-size:10.5px;color:var(--mut)}
.bm-gap .g-ser-lost{display:none;font-family:Poppins,system-ui,sans-serif;font-size:11px}
.bm-gap.b2:not(.b3) .g-ser-known{display:none}
.bm-gap.b2:not(.b3) .g-ser-lost{display:inline}
.bm-gap.b2:not(.b3) .g-token-model{color:var(--mut)}

/* captions: one grid cell, the current one visible */
.bm-gap .g-caps{display:grid}
.bm-gap .g-capt{grid-area:1/1;visibility:hidden}
.bm-gap .g-capt.is-on{visibility:visible}
.bm-gap.go .g-capt.is-on{animation:g-in .35s var(--bm-ease) both}
.bm-gap .g-cap-t{margin:0;font-size:clamp(21px,2vw,26px);line-height:1.12;font-weight:800;letter-spacing:-.03em;color:var(--txt);max-width:17ch}
.bm-gap .g-cap-b{margin:8px 0 0;font-size:14.5px;line-height:1.55;color:var(--mut);max-width:42ch}

/* the support call: present from the start, empty until the identity carries the context */
.bm-gap .g-ticket{border-radius:14px;background:var(--tk);box-shadow:0 0 0 1px var(--rule),0 24px 50px -30px rgba(24,19,67,.45);padding:13px 16px 4px;font-size:13px}
.bm-gap:not(.b2) .g-ticket{opacity:.55;box-shadow:0 0 0 1px var(--rule)}
.bm-gap .g-ticket-head{display:flex;flex-wrap:wrap;justify-content:space-between;align-items:center;gap:4px 10px}
.bm-gap .g-ticket-title{font-weight:700;color:var(--txt);letter-spacing:-.01em}
.bm-gap .g-st{display:none;align-items:center;gap:6px;font-size:11.5px;font-weight:500;color:var(--mut)}
.bm-gap .g-st i{display:block;width:8px;height:8px;border-radius:50%}
.bm-gap .g-st-empty i{box-shadow:inset 0 0 0 1.5px var(--faint)}
.bm-gap .g-st-full i{background:var(--bm-gradient)}
.bm-gap.b2:not(.b3) .g-st-empty{display:inline-flex}
.bm-gap.b3 .g-st-full{display:inline-flex;color:var(--txt)}
.bm-gap .g-ticket-quote{margin-top:5px;color:var(--mut);font-size:12.5px;visibility:hidden}
.bm-gap.b2 .g-ticket-quote{visibility:visible}
.bm-gap .g-rows{margin:9px 0 0;padding:0}
.bm-gap .g-row{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1.2fr);gap:12px;padding:7px 0;border-top:1px solid var(--rule);align-items:center;min-height:34px}
.bm-gap .g-row dt{color:var(--mut);font-size:12px;line-height:1.3}
.bm-gap .g-row dd{margin:0;color:var(--txt);font-weight:600;font-size:12.5px;line-height:1.3;letter-spacing:-.005em;min-width:0}
.bm-gap .g-row dd{display:grid;align-items:center}
.bm-gap .g-v-empty,.bm-gap .g-v{grid-area:1/1}
.bm-gap .g-v-empty{display:block;height:1px;border-bottom:1.5px dashed var(--qb);width:70%;align-self:center}
.bm-gap .g-v{visibility:hidden}
.bm-gap .g-nw{white-space:nowrap}
.bm-gap.b3 .g-v-empty{visibility:hidden}
.bm-gap.b3 .g-v{visibility:visible;animation:g-in .25s var(--bm-ease) calc(450ms + var(--i)*130ms) both}

@keyframes g-cut{from{visibility:hidden}to{visibility:visible}}
@keyframes g-in{0%{visibility:hidden;transform:translateY(6px)}1%{visibility:visible;transform:translateY(6px)}100%{visibility:visible;transform:none}}
@keyframes g-in-q{0%{visibility:hidden;transform:translateY(calc(-50% + 6px))}1%{visibility:visible;transform:translateY(calc(-50% + 6px))}100%{visibility:visible;transform:translateY(-50%)}}

/* compact */
.bm-gap.is-compact{padding:16px 20px 20px}
.bm-gap.is-compact .g-cap-t{font-size:clamp(19px,1.8vw,22px)}

/* vertical (narrow) */
.bm-gap.is-vert{padding:14px 14px 16px;border-radius:16px}
.bm-gap.is-vert .g-steps{gap:8px;margin-bottom:12px}
.bm-gap.is-vert .g-step{grid-template-columns:auto 1fr;column-gap:5px}
.bm-gap.is-vert .g-step-bar{margin-bottom:7px}
.bm-gap.is-vert .g-step-n{font-size:11px}
.bm-gap.is-vert .g-step-l{font-size:11px;line-height:1.25}
.bm-gap.is-vert .g-body{gap:14px}
.bm-gap.is-vert .g-cap-t{font-size:20px}
.bm-gap.is-vert .g-cap-b{font-size:14px;margin-top:6px}
.bm-gap.is-vert .g-ticket{padding:11px 12px 2px}
.bm-gap.is-vert .g-ticket-quote{font-size:12px}
.bm-gap.is-vert .g-row{grid-template-columns:minmax(0,.9fr) minmax(0,1.1fr);gap:10px;padding:6px 0;min-height:30px}
.bm-gap.is-vert .g-row dt{font-size:11.5px}
.bm-gap.is-vert .g-row dd{font-size:12px}

/* reduced motion: no transitions, no entrances; the final beat is shown */
.bm-gap.rm *,.bm-gap.rm *::before,.bm-gap.rm *::after{transition:none!important;animation:none!important}
`;
